import React from 'react';
import { Link } from 'react-router-dom';
import { INDUSTRY_ICONS } from '../../data/industriesData';
import { CHAPTER_COUNT } from './storyData';
import { SetNear } from './StudioSet';
import { Station } from './SectorStations';
import { SkylineFar, SkylineDeep } from './SectorSkyline';

// ─── STAGE VISUAL ─────────────────────────────────────────────────────────────
// Every chapter's artwork is mounted at once and left alone; GSAP raises and
// lowers them. Memoised on the layout, so a scroll never re-renders it — React
// owns the markup, GSAP owns the motion, and the two never touch the same
// property.
//
// ── what a sector looks like ──
//
// The sector's place stands behind — the hospital, the grid, the plant, the
// campus — and its services rise through the frame in front of it, one at a
// time, as REAL RENDERS: the actual neonatal warmer, the actual generator set
// with its dashboard. Each comes up from below the floor, settles under its
// own light, holds, and carries on up and out while the next is already
// rising.
//
// The board never moves: the robot arm is a fixed WebGL overlay aiming at that
// exact point, so the world moves and it does not. Which is also the better
// read — one board, and every service it powers passing through.
//
//   .zx-chapter               opacity only
//     .zx-chapter__depth      the sector-to-sector fly-through (vertical)
//       svg[data-l=bay]       the landing pad
//       svg[data-l=site]      the set, in three depth strips:
//                               [data-strip=far ]  0.30x  backdrop panels
//                               [data-strip=mid ]  1.00x  light and shadow
//                               [data-strip=near]  1.72x  streaks past the lens
//                             plus the floor and the power feed, which do NOT
//                             travel — they belong to the board's end of it.
//       div[data-l=work]      the renders themselves, and their captions
//
// The renders are HTML `<img>` rather than SVG `<image>`: they get real lazy
// loading, real decoding hints, and the browser's own downscaling. They ride a
// second [data-strip=mid] element so they travel in lockstep with the light
// and shadow drawn under them.

function CoreArt({ chapter, size }) {
  if (chapter.img) {
    return (
      <img
        src={chapter.img}
        alt=""
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  const Icon = INDUSTRY_ICONS[chapter.id];
  if (!Icon) return null;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ transform: `scale(${size / 32})`, display: 'flex' }}>
        <Icon c={chapter.accent} />
      </div>
    </div>
  );
}

/* ── THE BAY ────────────────────────────────────────────────────────────────
   The landing pad, and the only place the two halves of this section
   physically meet: the robot arm — a WebGL overlay that knows nothing about
   this DOM — solves its set-down pose against exactly this point, in world
   metres, out of exactly the same formula (see `bay` in layout.js and
   `aimX`/`aimY` in PcbJourney). Move it here without moving it there and the
   board lands beside its own pad. */
function Bay({ bay, accent, id }) {
  const x0 = bay.x - bay.w / 2;
  const y0 = bay.y - bay.h / 2;

  return (
    <g className="zx-bay" data-bay>
      <g className="zx-bay__breathe">
        <ellipse className="zx-bay__pool" data-bay-pool
          cx={bay.x} cy={bay.y} rx={bay.w * 0.82} ry={bay.h * 0.86}
          fill={`url(#zx-pool-${id})`} />
      </g>
      <g className="zx-bay__cross" data-bay-cross stroke={accent}>
        <line x1={bay.x - bay.w * 0.30} y1={bay.y} x2={bay.x + bay.w * 0.30} y2={bay.y} />
        <line x1={bay.x} y1={bay.y - bay.h * 0.44} x2={bay.x} y2={bay.y + bay.h * 0.44} />
      </g>
      <rect className="zx-bay__socket" data-bay-socket
        x={x0} y={y0} width={bay.w} height={bay.h}
        rx={Math.min(10, bay.h * 0.28)} stroke={accent} />
      <circle className="zx-bay__shock" data-bay-shock
        cx={bay.x} cy={bay.y} r={Math.max(bay.w, bay.h) * 0.5} stroke={accent} />
    </g>
  );
}

function StageVisual({ chapters, layouts }) {
  return (
    <>
      {chapters.map((ch, c) => {
        const L = layouts[c];
        if (!L) return null;

        const hubSize = Math.round(L.core.r * 2);
        const artSize = Math.round(L.core.r * 1.15);
        const totalChapters = String(CHAPTER_COUNT).padStart(2, '0');
        const n = ch.services.length;

        return (
          <div
            className="zx-chapter"
            key={ch.id}
            data-chapter={c}
            data-settled="false"
            data-seated="false"
            style={{ '--chapter-accent': ch.accent }}
          >
            <div className="zx-chapter__depth" data-depth>
              {/* ── layer 1: the bay ── */}
              <svg className="zx-chapter__svg" data-l="bay"
                viewBox={`0 0 ${L.width} ${L.height}`} focusable="false" aria-hidden="true">
                <defs>
                  <radialGradient id={`zx-pool-${ch.id}`}>
                    <stop offset="0%" stopColor={ch.accent} stopOpacity="0.30" />
                    <stop offset="55%" stopColor={ch.accent} stopOpacity="0.11" />
                    <stop offset="100%" stopColor={ch.accent} stopOpacity="0" />
                  </radialGradient>
                </defs>
                <Bay bay={L.bay} accent={ch.accent} id={ch.id} />
              </svg>

              {/* ── layer 2: the set ── */}
              <svg className="zx-chapter__svg" data-l="site"
                viewBox={`0 0 ${L.width} ${L.height}`} focusable="false" aria-hidden="true">
                <defs>
                  {/* The pool of light a service stands in. Wide and soft, so
                      the render separates from the cove without a hard edge
                      anywhere — a hard edge is the thing that would make it
                      look pasted on. */}
                  <radialGradient id={`zx-key-${ch.id}`}>
                    <stop offset="0%" stopColor={ch.accent} stopOpacity="0.26" />
                    <stop offset="45%" stopColor={ch.accent} stopOpacity="0.10" />
                    <stop offset="100%" stopColor={ch.accent} stopOpacity="0" />
                  </radialGradient>
                  {/* Every block is filled with this rather than a flat colour.
                      Light at the top, deeper at the base: a block with a
                      gradient has a form, a block with one colour is a swatch,
                      and that difference is most of what separated the first
                      drawn version from this one. */}
                  <linearGradient id={`zx-blk-${ch.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ch.accent} stopOpacity="0.62" />
                    <stop offset="52%" stopColor={ch.accent} stopOpacity="0.44" />
                    <stop offset="100%" stopColor={ch.accent} stopOpacity="0.30" />
                  </linearGradient>

                  {/* Contact shadow. Darkest directly under the subject and
                      gone within its own width — which is what a soft box
                      overhead actually produces, and what makes the thing look
                      like it is resting on the floor rather than floating. */}
                  <radialGradient id={`zx-shadow-${ch.id}`}>
                    <stop offset="0%" stopColor="#0A0A14" stopOpacity="0.30" />
                    <stop offset="55%" stopColor="#0A0A14" stopOpacity="0.10" />
                    <stop offset="100%" stopColor="#0A0A14" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <Bay bay={L.bay} accent={ch.accent} id={ch.id} />
              </svg>

              {/* ── layer 2: the set ── */}
              <svg className="zx-chapter__svg" data-l="site"
                viewBox={`0 0 ${L.width} ${L.height}`} focusable="false" aria-hidden="true">
                <defs>
                  {/* The pool of light a service stands in. Wide and soft, so
                      the render separates from the cove without a hard edge
                      anywhere — a hard edge is the thing that would make it
                      look pasted on. */}
                  <radialGradient id={`zx-key-${ch.id}`}>
                    <stop offset="0%" stopColor={ch.accent} stopOpacity="0.26" />
                    <stop offset="45%" stopColor={ch.accent} stopOpacity="0.10" />
                    <stop offset="100%" stopColor={ch.accent} stopOpacity="0" />
                  </radialGradient>
                  {/* Contact shadow. Darkest directly under the subject and
                      gone within its own width — which is what a soft box
                      overhead actually produces, and what makes the thing look
                      like it is resting on the floor rather than floating. */}
                  <radialGradient id={`zx-shadow-${ch.id}`}>
                    <stop offset="0%" stopColor="#0A0A14" stopOpacity="0.30" />
                    <stop offset="55%" stopColor="#0A0A14" stopOpacity="0.10" />
                    <stop offset="100%" stopColor="#0A0A14" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* The floor and the horizon are the BACKGROUND's, not this
                    layer's — see SectorWorld. A second ground line here would
                    sit on top of the other at every viewport where the two
                    disagreed by a pixel. */}

                {/* MID — the light and the shadow each service stands in. The
                    render itself is an <img> in the work layer above, riding
                    the same strip. */}
                {/* DEEP — the horizon, 0.14x. Massing only: no windows, no
                    marks, no structure. Detail is withheld on purpose, because
                    detail is what makes a distant thing read as near.

                    Pieces are laid at 0.55x the station spacing so their
                    silhouettes overlap into one continuous ridge rather than a
                    row of separated objects. */}
                <g className="zx-set__deep" data-strip="deep">
                  {Array.from({ length: L.stations.length * 2 + 3 }, (_, j) => (
                    <g key={j}
                      transform={`translate(${(j - 1) * L.gap * 0.55} ${L.groundY - 6}) `
                        + `scale(${L.stationScale * 0.50})`}>
                      <SkylineDeep sectorId={ch.id} index={j} />
                    </g>
                  ))}
                </g>

                {/* FAR — the sector's own landscape, 0.30x. This is what makes
                    the section ABOUT something: a hospital campus, a generating
                    site, a working plant, a university. Same block primitive as
                    the installations in front of it, so the whole frame is one
                    material, and half-gap spacing so it is a place rather than
                    a row of props. */}
                <g className="zx-set__far" data-strip="far">
                  {Array.from({ length: L.stations.length * 2 + 2 }, (_, j) => {
                    /* Alternate pieces are a SECOND ROW, not a repeat: smaller,
                       fainter, and standing a few pixels higher so their bases
                       are hidden behind the row in front.

                       Without this the strip is one flat wall of equal-value
                       buildings — every silhouette the same weight, which reads
                       as wallpaper however well each piece is drawn. Three
                       cues moving together (size, value, base height) is what
                       turns a row of objects into a town. */
                    const back = j % 2 === 1;
                    return (
                      <g key={j} opacity={back ? 0.58 : 1}
                        transform={`translate(${(j - 1) * L.gap * 0.5} `
                          + `${L.groundY - (back ? 15 : 2)}) `
                          + `scale(${L.stationScale * (back ? 0.53 : 0.68)})`}>
                        <SkylineFar sectorId={ch.id} index={j}
                          gradientId={`zx-blk-${ch.id}`} />
                      </g>
                    );
                  })}
                </g>

                <g className="zx-set__mid" data-strip="mid">
                  {ch.services.map((svc, i) => (
                    /* Two groups: the outer one is the position and GSAP never
                       touches it, the inner one is what arrives. GSAP writes
                       the `transform` ATTRIBUTE, so an element it animates
                       cannot also carry its own placement there. */
                    <g key={svc.slug}
                      transform={`translate(${L.stations[i].x} ${L.groundY})`}>
                      <g data-station={i} data-lit="false">
                        <ellipse className="zx-set__key"
                          cx="0" cy={-L.rigH * 0.46} rx={L.rigW * 0.62} ry={L.rigH * 0.56}
                          fill={`url(#zx-key-${ch.id})`} />
                        <ellipse className="zx-set__shadow"
                          cx="0" cy="4" rx={L.rigW * 0.40} ry={Math.max(12, L.rigH * 0.048)}
                          fill={`url(#zx-shadow-${ch.id})`} />
                        <g transform={`scale(${L.stationScale})`}>
                          <Station sectorId={ch.id} slug={svc.slug} index={i}
                            gradientId={`zx-blk-${ch.id}`} />
                        </g>
                      </g>
                    </g>
                  ))}
                </g>

                {/* The feed: board to whatever is standing at the viewing
                    position. Fixed, because the thing it points at is always in
                    the same place; it is the world that moves past it. */}
                <path className="zx-feed" data-feed d={L.feedD} stroke={ch.accent}
                  strokeDasharray={L.feedLen} strokeDashoffset={L.feedLen} />
                <circle className="zx-feed__plug" data-feed-plug
                  cx={L.connector.x} cy={L.connector.y} r="5" fill={ch.accent} />

                {/* NEAR — what the lens passes, 1.72x. Offset half a gap so it
                    falls BETWEEN the services rather than across them. */}
                {/* Foreground the lens passes. It is the only thing left that
                    still travels sideways, and it does so continuously, so
                    there is always something crossing the frame between one
                    service rising and the next. */}
                <g className="zx-set__near" data-strip="near">
                  {L.stations.map(st => (
                    <g key={st.index}
                      transform={`translate(${st.x + L.gap * 0.5} ${L.groundY + 30})`}>
                      <SetNear index={st.index} />
                    </g>
                  ))}
                </g>
              </svg>

              {/* ── layer 3: the renders, and their captions ── */}
              {/* The renders travel left as the camera moves on, and the
                  board's end of the stage is where the robot arm is standing.
                  Fading them out before they get there keeps a departing
                  service from sliding across the machine that delivered it -
                  and reads correctly anyway, as walking out of the light. */}
              <div
                className="zx-chapter__work"
                data-l="work"
                style={{ '--zx-fade-l': `${((L.laneL + 40) / L.width * 100).toFixed(1)}%` }}
              >
                <div className="zx-caps" data-strip="mid">
                  {ch.services.map((svc, i) => (
                    <Link
                      key={svc.slug}
                      to={svc.to}
                      className="zx-cap"
                      data-cap={i}
                      data-arrived="false"
                      tabIndex={-1}
                      style={{
                        left: L.stations[i].x,
                        top: L.groundY + 20,
                        width: L.labelWidth,
                        marginLeft: -L.labelWidth / 2,
                        '--node-accent': ch.accent,
                      }}
                    >
                      <span className="zx-cap__no" style={{ color: ch.accent }}>{svc.id}</span>
                      <span className="zx-cap__title">{svc.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* ── the sector core, over the bay ── */}
              <div className="zx-core" data-core style={{ left: L.core.x, top: L.core.y }}>
                <div className="zx-core__float">
                  <div className="zx-core__hub" style={{ width: hubSize, height: hubSize }}>
                    <span className="zx-core__ring zx-core__ring--a"
                      style={{ width: hubSize * 1.24, height: hubSize * 1.24, borderColor: `${ch.accent}38` }} />
                    <span className="zx-core__ring zx-core__ring--b"
                      style={{ width: hubSize * 1.62, height: hubSize * 1.62, borderColor: `${ch.accent}1C` }} />
                    <span className="zx-core__halo" data-halo
                      style={{
                        width: hubSize, height: hubSize,
                        background: `radial-gradient(circle, ${ch.accent}30 0%, ${ch.accent}00 68%)`,
                      }} />
                    <div className="zx-core__art"
                      style={{ filter: `drop-shadow(0 10px 24px ${ch.accent}4D)` }}>
                      <CoreArt chapter={ch} size={artSize} />
                    </div>
                    <div className="zx-core__caption">
                      <span className="zx-core__label" style={{ color: ch.accent }}>{ch.short}</span>
                      <span className="zx-core__kicker">
                        Sector {ch.id} / {totalChapters} &middot; {n} services
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

// Layouts are rebuilt only when the stage box actually changes size, so this
// keeps the whole visual out of the scroll path entirely.
export default React.memo(StageVisual);
